import React, { useEffect, useRef } from "react";
import { DatasetIcon } from "../../../Common/SvgIcons/WorkflowIcons";

const SPLIT_COLORS = {
  train: "#2563eb",
  val: "#7c3aed",
  test: "#22c55e",
};

const SPLIT_PRESETS = [
  { id: "80/10/10", train: 0.8, val: 0.1, test: 0.1 },
  { id: "70/15/15", train: 0.7, val: 0.15, test: 0.15 },
];

export const CUSTOM_SPLIT_PRESET = SPLIT_PRESETS[0];

const normalizeName = (value = "") =>
  String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");

export const getReadableExperimentName = (name = "") =>
  String(name || "")
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());

const isSplitTypeName = (name = "", title = "") => {
  const normalized = normalizeName(`${name} ${title}`);
  return (
    normalized.includes("splittype") ||
    (normalized.includes("split") && normalized.includes("type"))
  );
};

export const isSplitRatioName = (name = "", title = "") => {
  const normalized = normalizeName(`${name} ${title}`);
  if (!normalized || isSplitTypeName(name, title)) return false;

  return (
    (normalized.includes("train") && normalized.includes("split")) ||
    (normalized.includes("val") && normalized.includes("split")) ||
    (normalized.includes("test") && normalized.includes("split"))
  );
};

const getSplitKind = (name = "", title = "") => {
  const normalized = normalizeName(`${name} ${title}`);
  if (normalized.includes("train")) return "train";
  if (normalized.includes("val")) return "val";
  if (normalized.includes("test")) return "test";
  return "";
};

export const isSplitObjectSchema = (schemaNode) => {
  const properties = schemaNode?.properties;
  if (!properties || schemaNode.type !== "object") return false;

  const keys = Object.keys(properties);
  const splitKeys = keys.filter((key) =>
    isSplitRatioName(key, properties[key]?.title)
  );
  const typeKeys = keys.filter((key) =>
    isSplitTypeName(key, properties[key]?.title)
  );

  return (
    splitKeys.length >= 2 &&
    !keys.some((key) => isDatasetFieldName(key, properties[key]?.title))
  );
};

const toSplitNumber = (value) => {
  if (value === "" || value === undefined || value === null) return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

const formatPercent = (ratio) => {
  if (ratio === null || ratio === undefined || !Number.isFinite(ratio)) return "—";
  const percent = clampRatio(ratio, 0, 1) * 100;
  const rounded = Math.round(percent * 10) / 10;
  return `${Number.isInteger(rounded) ? rounded : rounded.toFixed(1)}%`;
};

const MIN_SPLIT_RATIO = 0.01;

const asRatio = (value) => {
  const numeric = toSplitNumber(value);
  if (numeric === null) return 0;
  if (numeric > 1 && numeric <= 100) return numeric / 100;
  if (numeric > 100) return 0;
  return Math.max(0, numeric);
};

const splitValuesToRatios = (trainValue, valValue, testValue) => {
  const train = Number(trainValue) || 0;
  const val = Number(valValue) || 0;
  const test = Number(testValue) || 0;
  const sum = train + val + test;
  if (sum <= 0) return { train: 0, val: 0, test: 0 };
  if (Math.abs(sum - 1) <= 0.051) {
    return {
      train: clampRatio(train, 0, 1),
      val: clampRatio(val, 0, 1),
      test: clampRatio(test, 0, 1),
    };
  }
  return {
    train: train / sum,
    val: val / sum,
    test: test / sum,
  };
};

const getSplitImageCounts = (ratios, totalImages) => {
  const total = Number(totalImages);
  if (!Number.isFinite(total) || total < 1) {
    return { train: null, val: null, test: null };
  }
  const train = Math.floor(total * clampRatio(ratios.train, 0, 1));
  const val = Math.floor(total * clampRatio(ratios.val, 0, 1));
  const test = Math.max(0, Math.round(total) - train - val);
  return { train, val, test };
};

const getSplitRatioValidationMessage = (ratios) => {
  const train = Number(ratios?.train) || 0;
  const val = Number(ratios?.val) || 0;
  const test = Number(ratios?.test) || 0;
  const sum = Number((train + val + test).toFixed(4));

  if (Math.abs(sum - 1) >= 0.001) {
    return "Train, val, and test must add up to 100% before continuing.";
  }
  if (train <= 0) {
    return "Train cannot be 0%. Set a train split greater than 0.";
  }
  if (val <= 0) {
    return "Val cannot be 0%. Set a validation split greater than 0.";
  }
  if (val > train + 0.0001) {
    return "Val cannot be greater than train.";
  }
  if (test > train + 0.0001) {
    return "Test cannot be greater than train.";
  }
  return "";
};

const ratioToSchemaValue = (ratio, schemaNode = {}) => {
  const max = Number(schemaNode?.maximum);
  if (Number.isFinite(max) && max > 1) {
    return Math.round(clampRatio(ratio, 0, 1) * max * 100) / 100;
  }
  return Math.round(clampRatio(ratio, 0, 1) * 100) / 100;
};

const formatSplitTypeLabel = (label = "") =>
  String(label || "")
    .replace(/\s*\(\s*loaded from data\s*\)\s*/gi, " ")
    .replace(/\s+/g, " ")
    .trim();

const visitSchemaTree = (schemaNode, formDataNode, path, visit) => {
  if (!schemaNode || typeof schemaNode !== "object") return;

  visit(schemaNode, formDataNode, path);

  if (schemaNode.properties && typeof schemaNode.properties === "object") {
    Object.entries(schemaNode.properties).forEach(([key, child]) => {
      visitSchemaTree(child, formDataNode?.[key], [...path, key], visit);
    });
  }

  if (schemaNode.items && typeof schemaNode.items === "object") {
    visitSchemaTree(schemaNode.items, formDataNode, path, visit);
  }

  [...(schemaNode.oneOf || []), ...(schemaNode.anyOf || []), ...(schemaNode.allOf || [])].forEach(
    (branch) => visitSchemaTree(branch, formDataNode, path, visit)
  );

  if (schemaNode.dependencies && typeof schemaNode.dependencies === "object") {
    const parentData =
      formDataNode && typeof formDataNode === "object" && !Array.isArray(formDataNode)
        ? formDataNode
        : undefined;
    Object.values(schemaNode.dependencies).forEach((dependency) => {
      if (Array.isArray(dependency?.oneOf)) {
        dependency.oneOf.forEach((branch) =>
          visitSchemaTree(branch, parentData, path, visit)
        );
      } else if (dependency && typeof dependency === "object") {
        visitSchemaTree(dependency, parentData, path, visit);
      }
    });
  }

  if (schemaNode.then && typeof schemaNode.then === "object") {
    visitSchemaTree(schemaNode.then, formDataNode, path, visit);
  }

  if (schemaNode.else && typeof schemaNode.else === "object") {
    visitSchemaTree(schemaNode.else, formDataNode, path, visit);
  }
};

const findSchemaNode = (schemaNode, predicate, path = []) => {
  let match = null;
  visitSchemaTree(schemaNode, undefined, path, (node, _formData, currentPath) => {
    if (!match && predicate(node, currentPath)) {
      match = { schema: node, path: currentPath };
    }
  });
  return match;
};

const looksLikeVersionId = (value) => {
  const text = String(value ?? "").trim();
  if (!text) return false;
  if (/^[a-f0-9]{24}$/i.test(text)) return true;
  if (text.length > 20 && !/^\d+(\.\d+)*$/.test(text)) return true;
  return false;
};

const getOptionLabel = (schemaNode, value) => {
  if (value === undefined || value === null || value === "") return "";
  const enums = schemaNode?.enum || [];
  const names = schemaNode?.enumNames || enums;
  const index = enums.findIndex((item) => String(item) === String(value));
  if (index >= 0) return String(names[index] || value);

  const branches = [...(schemaNode?.oneOf || []), ...(schemaNode?.anyOf || [])];
  for (const branch of branches) {
    const branchValue = branch?.const ?? branch?.enum?.[0];
    if (branchValue !== undefined && String(branchValue) === String(value)) {
      return String(branch?.title || branch?.enumNames?.[0] || value);
    }
    const nested = getOptionLabel(branch, value);
    if (nested && nested !== String(value)) return nested;
  }

  if (schemaNode?.properties && typeof schemaNode.properties === "object") {
    for (const child of Object.values(schemaNode.properties)) {
      const nested = getOptionLabel(child, value);
      if (nested && nested !== String(value)) return nested;
    }
  }

  return String(value);
};

const resolveDisplayLabel = (hits = [], value, schema) => {
  if (value === undefined || value === null || value === "") return "";

  let fallback = "";
  for (const hit of hits) {
    const label = getOptionLabel(hit?.node, value);
    if (!label) continue;
    if (label !== String(value)) return label;
    if (!fallback) fallback = label;
  }

  if (schema && looksLikeVersionId(value)) {
    let found = "";
    visitSchemaTree(schema, undefined, [], (node) => {
      if (found) return;
      const label = getOptionLabel(node, value);
      if (label && label !== String(value)) {
        found = label;
      }
    });
    if (found) return found;
  }

  return fallback || String(value);
};

const isDatasetFieldName = (name = "", title = "") => {
  const leaf = normalizeName(`${name} ${title}`);
  return leaf.includes("dataset") && !leaf.includes("version") && !leaf.includes("split");
};

const isVersionFieldName = (name = "", title = "") => {
  const leaf = normalizeName(`${name} ${title}`);
  return (
    (leaf.includes("dataset") && leaf.includes("version")) ||
    leaf === "version" ||
    leaf === "datasetversion"
  );
};

const isImageCountFieldName = (name = "", title = "") => {
  const leaf = normalizeName(`${name} ${title}`);
  return (
    (leaf.includes("image") &&
      (leaf.includes("total") ||
        leaf.includes("count") ||
        leaf.includes("available") ||
        leaf.includes("num"))) ||
    leaf === "totalimages" ||
    leaf === "numimages" ||
    leaf === "availableimages"
  );
};

const getValueAtPath = (source, path = []) =>
  path.reduce(
    (current, key) => (current && typeof current === "object" ? current[key] : undefined),
    source
  );

const setValueAtPath = (source, path = [], value) => {
  if (!path.length) return value;
  const [head, ...rest] = path;
  const base =
    source && typeof source === "object" && !Array.isArray(source) ? { ...source } : {};
  base[head] = setValueAtPath(base[head], rest, value);
  return base;
};

const findSplitEntries = (schemaNode, formDataNode) => {
  const entries = [];
  const seen = new Set();

  visitSchemaTree(schemaNode, formDataNode, [], (node, value, path) => {
    const key = path[path.length - 1];
    if (!isSplitRatioName(key, node?.title)) return;
    const signature = path.join(".");
    if (seen.has(signature)) return;
    seen.add(signature);
    entries.push({
      path,
      kind: getSplitKind(key, node?.title),
      schema: node,
      value,
    });
  });

  return entries;
};

const findSplitTypePaths = (schemaNode) => {
  const paths = [];
  const seen = new Set();

  visitSchemaTree(schemaNode, undefined, [], (node, _value, path) => {
    const key = path[path.length - 1];
    if (!isSplitTypeName(key, node?.title)) return;
    const signature = path.join(".");
    if (seen.has(signature)) return;
    seen.add(signature);
    paths.push({ path, schema: node });
  });

  return paths;
};

const applyPresetToMatchingKeys = (data, preset) => {
  if (!data || typeof data !== "object" || Array.isArray(data)) return data;

  return Object.fromEntries(
    Object.entries(data).map(([key, value]) => {
      const kind = getSplitKind(key, "");
      if (kind && preset[kind] !== undefined && isSplitRatioName(key, "")) {
        return [key, preset[kind]];
      }
      if (value && typeof value === "object" && !Array.isArray(value)) {
        return [key, applyPresetToMatchingKeys(value, preset)];
      }
      return [key, value];
    })
  );
};

const applyValueToMatchingKeys = (data, predicate, nextValue) => {
  if (!data || typeof data !== "object" || Array.isArray(data)) return data;

  return Object.fromEntries(
    Object.entries(data).map(([key, value]) => {
      if (predicate(key)) {
        return [key, nextValue];
      }
      if (value && typeof value === "object" && !Array.isArray(value)) {
        return [key, applyValueToMatchingKeys(value, predicate, nextValue)];
      }
      return [key, value];
    })
  );
};

export const applySplitPresetToFormData = (schema, formData, preset) => {
  const current = formData && typeof formData === "object" ? formData : {};
  const entries = findSplitEntries(schema, current);
  if (!entries.length) {
    return applyPresetToMatchingKeys(current, preset);
  }

  return entries.reduce((next, entry) => {
    const nextValue = preset[entry.kind];
    if (nextValue === undefined) return next;
    return setValueAtPath(next, entry.path, nextValue);
  }, current);
};

export const applySplitTypeToFormData = (schema, formData, value) => {
  const current = formData && typeof formData === "object" ? formData : {};
  const paths = findSplitTypePaths(schema);
  if (!paths.length) {
    return applyValueToMatchingKeys(current, (key) => isSplitTypeName(key, ""), value);
  }

  return paths.reduce((next, entry) => setValueAtPath(next, entry.path, value), current);
};

const isEmptySplitValue = (value) =>
  value === "" || value === undefined || value === null;

export const getDefaultSplitTypeValue = (schemaNode = {}) => {
  const options = getEnumOptions(schemaNode);
  const defaultOption = options.find(
    (option) =>
      /default|source/i.test(String(option.value)) ||
      /default|source/i.test(String(option.label))
  );
  if (defaultOption) return defaultOption.value;
  if (!isEmptySplitValue(schemaNode.default)) return schemaNode.default;
  return options[0]?.value;
};

const isDefaultSplitChoice = (value, schemaNode = {}) => {
  const options = getEnumOptions(schemaNode);
  const option = options.find((item) => String(item.value) === String(value));
  return /default|source/i.test(`${value || ""} ${option?.label || ""}`);
};

export const getCustomSplitTypeValue = (schemaNode = {}) => {
  const options = getEnumOptions(schemaNode);
  const customOption = options.find(
    (option) =>
      /custom|change/i.test(String(option.value)) ||
      /custom|change/i.test(String(option.label))
  );
  return customOption?.value;
};

export const getCustomSplitTypeValueFromSchema = (schema) => {
  const hit = findSplitTypePaths(schema)[0];
  return hit ? getCustomSplitTypeValue(hit.schema) : undefined;
};

export const getSplitRatiosFromFormData = (schema, formData) => {
  const entries = findSplitEntries(schema, formData);
  return splitValuesToRatios(
    entries.find((entry) => entry.kind === "train")?.value,
    entries.find((entry) => entry.kind === "val")?.value,
    entries.find((entry) => entry.kind === "test")?.value
  );
};

export const isDefaultSplitSelected = (schema, formData) => {
  const typeHit = findSplitTypePaths(schema)[0];
  if (!typeHit) return false;
  const typeValue = getValueAtPath(formData, typeHit.path);
  return isDefaultSplitChoice(typeValue, typeHit.schema);
};

export const getSplitValidationMessage = (
  schema,
  formData,
  { allowZeroDefaultSplits = false } = {}
) => {
  const entries = findSplitEntries(schema, formData);
  if (!entries.length) return "";

  const ratios = splitValuesToRatios(
    entries.find((entry) => entry.kind === "train")?.value,
    entries.find((entry) => entry.kind === "val")?.value,
    entries.find((entry) => entry.kind === "test")?.value
  );
  const sum = Number((ratios.train + ratios.val + ratios.test).toFixed(4));
  const ratioError = getSplitRatioValidationMessage(ratios);

  const typeHit = findSplitTypePaths(schema)[0];
  const typeValue = typeHit ? getValueAtPath(formData, typeHit.path) : undefined;
  const isDefault = typeHit ? isDefaultSplitChoice(typeValue, typeHit.schema) : false;

  if (
    isDefault &&
    allowZeroDefaultSplits &&
    Math.abs(sum - 1) < 0.001 &&
    ratios.train > 0 &&
    ratios.val <= ratios.train + 0.0001 &&
    ratios.test <= ratios.train + 0.0001
  ) {
    return "";
  }

  if (!ratioError) return "";

  if (isDefault && sum === 0) {
    return "Default split from this dataset is 0. Choose Use Custom Split to set Train / Val / Test, or select another dataset version.";
  }

  if (isDefault && Math.abs(sum - 1) >= 0.001) {
    return "Default split from this dataset is not valid. Train, val, and test must add up to 100%. Choose Use Custom Split to continue.";
  }

  if (isDefault) {
    return `Default split from this dataset is not valid. ${ratioError} Choose Use Custom Split to continue.`;
  }

  return ratioError;
};

export const hydrateSplitFormData = (schema, formData) => {
  let next = formData && typeof formData === "object" ? { ...formData } : {};

  findSplitTypePaths(schema).forEach(({ path, schema: node }) => {
    const current = getValueAtPath(next, path);
    const preferred = getDefaultSplitTypeValue(node);
    if (isEmptySplitValue(current) && !isEmptySplitValue(preferred)) {
      next = setValueAtPath(next, path, preferred);
    }
  });

  const entries = findSplitEntries(schema, next);
  const readSplitRaw = (entry) => {
    if (!entry?.path?.length) return undefined;
    const fromData = getValueAtPath(next, entry.path);
    if (!isEmptySplitValue(fromData)) return fromData;
    return entry.schema?.default;
  };
  const byKind = {
    train: entries.find((entry) => entry.kind === "train"),
    val: entries.find((entry) => entry.kind === "val"),
    test: entries.find((entry) => entry.kind === "test"),
  };
  const ratios = splitValuesToRatios(
    readSplitRaw(byKind.train),
    readSplitRaw(byKind.val),
    readSplitRaw(byKind.test)
  );

  entries.forEach((entry) => {
    if (!entry.kind || !entry.path?.length) return;
    next = setValueAtPath(
      next,
      entry.path,
      ratioToSchemaValue(ratios[entry.kind], entry.schema)
    );
  });

  return next;
};

export const mergeSplitValuesIntoFormData = (schema, target, source) => {
  let next = target && typeof target === "object" ? { ...target } : {};
  const fallback = source && typeof source === "object" ? source : {};

  findSplitTypePaths(schema).forEach(({ path, schema: node }) => {
    const current = getValueAtPath(next, path);
    if (!isEmptySplitValue(current)) return;
    const fromSource = getValueAtPath(fallback, path);
    const value = !isEmptySplitValue(fromSource)
      ? fromSource
      : getDefaultSplitTypeValue(node);
    if (!isEmptySplitValue(value)) {
      next = setValueAtPath(next, path, value);
    }
  });

  findSplitEntries(schema, fallback).forEach((entry) => {
    const current = getValueAtPath(next, entry.path);
    if (!isEmptySplitValue(current)) return;
    const fromSource = getValueAtPath(fallback, entry.path);
    if (!isEmptySplitValue(fromSource)) {
      next = setValueAtPath(next, entry.path, fromSource);
      return;
    }
    const schemaDefault = entry.schema?.default;
    if (schemaDefault !== undefined && schemaDefault !== null && schemaDefault !== "") {
      next = setValueAtPath(next, entry.path, schemaDefault);
    }
  });

  return hydrateSplitFormData(schema, next);
};

export const findEpochsPath = (schema) => {
  const match = findSchemaNode(schema, (node, path) => {
    const leaf = normalizeName(path[path.length - 1] || node?.title);
    return (
      ["number", "integer"].includes(node?.type) &&
      leaf.includes("epoch") &&
      !leaf.includes("step")
    );
  });

  return match?.path || null;
};

export const applyEpochPresetToFormData = (schema, formData, epochs) => {
  const path = findEpochsPath(schema);
  if (!path) return formData;
  return setValueAtPath(formData && typeof formData === "object" ? formData : {}, path, epochs);
};

const parseCountFromLabel = (label = "") => {
  const text = String(label);
  const parenMatch = text.match(/\(([\d,]+)\s*(imgs?|images|samples)?\)/i);
  if (parenMatch) {
    const numeric = Number(String(parenMatch[1]).replace(/,/g, ""));
    return Number.isFinite(numeric) ? numeric : null;
  }

  const explicitMatch = text.match(/([\d,]+)\s+(imgs?|images|samples)\b/i);
  if (!explicitMatch) return null;

  const numeric = Number(String(explicitMatch[1]).replace(/,/g, ""));
  return Number.isFinite(numeric) ? numeric : null;
};

const parseResolutionFromLabel = (label = "") => {
  const match = String(label).match(/(\d{2,5})\s*[x×]\s*(\d{2,5})/i);
  return match ? `${match[1]}x${match[2]}` : null;
};

export const getDatasetOverview = (schema, formData) => {
  const datasetHits = [];
  const versionHits = [];
  const imageHits = [];
  const classHits = [];

  visitSchemaTree(schema, formData, [], (node, value, path) => {
    const key = path[path.length - 1] || "";
    if (isDatasetFieldName(key, node?.title) && Array.isArray(node?.enum)) {
      datasetHits.push({ node, value, path });
    }
    if (isVersionFieldName(key, node?.title)) {
      versionHits.push({ node, value, path });
    }
    if (isImageCountFieldName(key, node?.title)) {
      imageHits.push({ node, value, path });
    }
    if (
      normalizeName(`${key} ${node?.title}`).includes("class") &&
      ["integer", "number"].includes(node?.type)
    ) {
      classHits.push({ node, value, path });
    }
  });

  const datasetHit =
    datasetHits.find((hit) => hit.value !== undefined && hit.value !== null && hit.value !== "") ||
    datasetHits[0];
  const datasetValue = datasetHit
    ? getValueAtPath(formData, datasetHit.path) ?? datasetHit.value
    : undefined;

  if (datasetValue === undefined || datasetValue === null || datasetValue === "") {
    return null;
  }

  const hasVersionValue = versionHits.some((hit) => {
    const current = getValueAtPath(formData, hit.path) ?? hit.value;
    return current !== undefined && current !== null && current !== "";
  });

  if (!hasVersionValue && formData && typeof formData === "object") {
    const scan = (data, trail = []) => {
      if (!data || typeof data !== "object" || Array.isArray(data)) return;
      Object.entries(data).forEach(([key, value]) => {
        if (isVersionFieldName(key, "") && value !== undefined && value !== null && value !== "") {
          versionHits.push({ node: {}, value, path: [...trail, key] });
        }
        if (isImageCountFieldName(key, "") && value != null) {
          imageHits.push({ node: {}, value, path: [...trail, key] });
        }
        scan(value, [...trail, key]);
      });
    };
    scan(formData);
  }

  const datasetLabel =
    resolveDisplayLabel(datasetHits, datasetValue, schema) || String(datasetValue);

  const versionCandidates = versionHits
    .map((hit) => ({
      hit,
      value: getValueAtPath(formData, hit.path) ?? hit.value,
    }))
    .filter(
      (entry) =>
        entry.value !== undefined && entry.value !== null && entry.value !== ""
    );

  const namedVersionEntry = versionCandidates.find((entry) => {
    const key = normalizeName(entry.hit.path?.[entry.hit.path.length - 1] || "");
    return key === "datasetversion" || key.endsWith("datasetversion");
  });
  const idVersionEntry = versionCandidates.find((entry) =>
    looksLikeVersionId(entry.value)
  );
  const humanVersionEntry = versionCandidates.find(
    (entry) => !looksLikeVersionId(entry.value)
  );

  // Keep ID for API lookups when available; fall back to the Dataset Version field.
  const versionValue =
    idVersionEntry?.value ??
    namedVersionEntry?.value ??
    versionCandidates[0]?.value;
  const versionLabel =
    (humanVersionEntry && String(humanVersionEntry.value)) ||
    resolveDisplayLabel(
      versionCandidates.map((entry) => entry.hit),
      versionValue,
      schema
    );
  const imageHit = imageHits.find((hit) => toSplitNumber(getValueAtPath(formData, hit.path) ?? hit.value) != null);
  const imageFromField = imageHit
    ? toSplitNumber(getValueAtPath(formData, imageHit.path) ?? imageHit.value)
    : null;
  const classHit = classHits.find((hit) => toSplitNumber(getValueAtPath(formData, hit.path) ?? hit.value) != null);

  const cleanedVersion = String(versionLabel || versionValue || "")
    .replace(/\s*\(([\d,]+)(?:\s*(?:imgs?|images|samples))?\)\s*$/i, "")
    .trim();

  return {
    label: String(datasetLabel),
    version:
      cleanedVersion || (versionValue != null ? String(versionValue) : null),
    datasetValue,
    versionValue: versionValue ?? null,
    total:
      imageFromField ??
      parseCountFromLabel(versionLabel) ??
      parseCountFromLabel(datasetLabel),
    resolution:
      parseResolutionFromLabel(versionLabel) || parseResolutionFromLabel(datasetLabel),
    classes: classHit
      ? toSplitNumber(getValueAtPath(formData, classHit.path) ?? classHit.value)
      : null,
  };
};

const roundRatio = (value) => Math.round(Math.max(0, value) * 100) / 100;

const clampRatio = (value, min, max) => Math.min(max, Math.max(min, value));

const ExperimentSplitVisual = ({
  title,
  required,
  rows,
  totalImages,
  onPreset,
  onChangeRatios,
  extraContent,
  disabled,
  errorMessage,
  allowZeroSplits = false,
}) => {
  const barRef = useRef(null);
  const dragRef = useRef(null);
  const ratiosRef = useRef({ train: 0.8, val: 0.1, test: 0.1 });
  const ratios = splitValuesToRatios(
    rows.find((row) => row.kind === "train")?.value,
    rows.find((row) => row.kind === "val")?.value,
    rows.find((row) => row.kind === "test")?.value
  );
  const sum = Number((ratios.train + ratios.val + ratios.test).toFixed(4));
  const canUseZeroSplits =
    allowZeroSplits &&
    Math.abs(sum - 1) < 0.001 &&
    ratios.train > 0 &&
    ratios.val <= ratios.train + 0.0001 &&
    ratios.test <= ratios.train + 0.0001;
  const ratioError = canUseZeroSplits
    ? ""
    : getSplitRatioValidationMessage(ratios);
  const isValid = !ratioError;
  const imageCounts = getSplitImageCounts(ratios, totalImages);
  const activePreset = SPLIT_PRESETS.find(
    (preset) =>
      Math.abs(preset.train - ratios.train) < 0.001 &&
      Math.abs(preset.val - ratios.val) < 0.001 &&
      Math.abs(preset.test - ratios.test) < 0.001
  );
  const canEdit = typeof onChangeRatios === "function" && !disabled;
  ratiosRef.current = ratios;
  const onChangeRatiosRef = useRef(onChangeRatios);
  onChangeRatiosRef.current = onChangeRatios;

  const emitRatios = (train, val, test) => {
    const nextTrain = roundRatio(train);
    const nextVal = roundRatio(val);
    const nextTest = roundRatio(1 - nextTrain - nextVal);
    onChangeRatiosRef.current?.({
      train: nextTrain,
      val: nextVal,
      test: Math.max(0, nextTest),
    });
  };

  const pointerToRatio = (clientX) => {
    const rect = barRef.current?.getBoundingClientRect();
    if (!rect?.width) return 0;
    return clampRatio((clientX - rect.left) / rect.width, 0, 1);
  };

  const applyFromClientX = (handle, clientX) => {
    const current = ratiosRef.current;
    const x = pointerToRatio(clientX);

    if (handle === "train") {
      // Move train/val edge. Keep splits valid: train/val > 0, test >= 0, val/test <= train.
      const train = clampRatio(x, MIN_SPLIT_RATIO, 1 - MIN_SPLIT_RATIO);
      const remaining = 1 - train;
      let test = clampRatio(
        current.test,
        0,
        Math.min(train, Math.max(0, remaining - MIN_SPLIT_RATIO))
      );
      let val = remaining - test;
      if (val > train) {
        val = train;
        test = Math.max(0, remaining - val);
      }
      if (val < MIN_SPLIT_RATIO) {
        val = MIN_SPLIT_RATIO;
        test = Math.max(0, remaining - val);
      }
      emitRatios(train, val, test);
      return;
    }

    // Move val/test edge. Raise train if needed so val/test can stay <= train.
    let train = Math.max(current.train, MIN_SPLIT_RATIO);
    const minFeasibleTrain = (1 + MIN_SPLIT_RATIO) / 3;
    if (train < minFeasibleTrain) {
      train = minFeasibleTrain;
    }
    const remaining = 1 - train;
    const minVal = Math.max(MIN_SPLIT_RATIO, remaining - train);
    const maxVal = Math.min(train, remaining);
    const val = clampRatio(x - train, minVal, Math.max(minVal, maxVal));
    emitRatios(train, val, remaining - val);
  };

  useEffect(() => {
    if (!canEdit) return undefined;

    const clientXFromEvent = (event) =>
      event.touches?.[0]?.clientX ?? event.changedTouches?.[0]?.clientX ?? event.clientX;

    const handleMove = (event) => {
      if (!dragRef.current) return;
      if (event.cancelable) event.preventDefault();
      applyFromClientX(dragRef.current, clientXFromEvent(event));
    };

    const stopDrag = () => {
      dragRef.current = null;
    };

    window.addEventListener("pointermove", handleMove, true);
    window.addEventListener("mousemove", handleMove, true);
    window.addEventListener("touchmove", handleMove, { capture: true, passive: false });
    window.addEventListener("pointerup", stopDrag, true);
    window.addEventListener("mouseup", stopDrag, true);
    window.addEventListener("touchend", stopDrag, true);
    window.addEventListener("pointercancel", stopDrag, true);
    return () => {
      window.removeEventListener("pointermove", handleMove, true);
      window.removeEventListener("mousemove", handleMove, true);
      window.removeEventListener("touchmove", handleMove, true);
      window.removeEventListener("pointerup", stopDrag, true);
      window.removeEventListener("mouseup", stopDrag, true);
      window.removeEventListener("touchend", stopDrag, true);
      window.removeEventListener("pointercancel", stopDrag, true);
    };
  }, [canEdit]);

  const startDrag = (handle) => (event) => {
    if (!canEdit) return;
    event.preventDefault();
    event.stopPropagation();
    dragRef.current = handle;
    applyFromClientX(handle, event.touches?.[0]?.clientX ?? event.clientX);
  };

  const startBarDrag = (event) => {
    if (!canEdit) return;
    if (event.target?.closest?.(".experiment-split-bar__handle")) return;
    const x = pointerToRatio(event.touches?.[0]?.clientX ?? event.clientX);
    const trainEdge = ratiosRef.current.train;
    const valEdge = ratiosRef.current.train + ratiosRef.current.val;
    const handle = Math.abs(x - trainEdge) <= Math.abs(x - valEdge) ? "train" : "val";
    startDrag(handle)(event);
  };

  return (
    <section className="experiment-split-card">
      <div className="experiment-split-card__header">
        <h5 className="experiment-split-card__title mb-0">
          {getReadableExperimentName(title || "Split Dataset")}
          {required ? <span className="text-danger ms-1">*</span> : null}
        </h5>
        {typeof onPreset === "function" && (
          <div className="experiment-split-presets" role="group" aria-label="Split presets">
            {SPLIT_PRESETS.map((preset) => (
              <button
                type="button"
                key={preset.id}
                className={`experiment-split-preset ${
                  activePreset?.id === preset.id ? "is-active" : ""
                }`}
                onClick={() => onPreset(preset)}
              >
                {preset.id}
              </button>
            ))}
          </div>
        )}
      </div>

      {extraContent}

      <div
        className={`experiment-split-bar ${canEdit ? "is-interactive" : ""}`}
        ref={barRef}
        role="group"
        aria-label="Train, validation, and test split"
        onPointerDown={startBarDrag}
        onMouseDown={startBarDrag}
        onTouchStart={startBarDrag}
      >
        <div className="experiment-split-bar__track">
          {["train", "val", "test"].map((kind) => (
            <span
              key={kind}
              className={`experiment-split-bar__segment experiment-split-bar__segment--${kind}`}
              style={{
                width: `${clampRatio(ratios[kind], 0, 1) * 100}%`,
                background: SPLIT_COLORS[kind],
              }}
            />
          ))}
        </div>
        {canEdit ? (
          <>
            <span
              className="experiment-split-bar__handle"
              style={{ left: `${clampRatio(ratios.train, 0, 1) * 100}%` }}
              role="slider"
              tabIndex={0}
              aria-label="Adjust train and validation split"
              onPointerDown={startDrag("train")}
              onMouseDown={startDrag("train")}
              onTouchStart={startDrag("train")}
            />
            <span
              className="experiment-split-bar__handle"
              style={{
                left: `${clampRatio(ratios.train + ratios.val, 0, 1) * 100}%`,
              }}
              role="slider"
              tabIndex={0}
              aria-label="Adjust validation and test split"
              onPointerDown={startDrag("val")}
              onMouseDown={startDrag("val")}
              onTouchStart={startDrag("val")}
            />
          </>
        ) : null}
      </div>

      <div className="experiment-split-legend">
        {["train", "val", "test"].map((kind) => {
          const count = imageCounts[kind];
          const label = kind === "train" ? "Train" : kind === "val" ? "Val" : "Test";
          return (
            <span key={kind} className={`experiment-split-legend__item is-${kind}`}>
              <span className="experiment-split-legend__label">{label}</span>
              <span className="experiment-split-legend__percent">
                {formatPercent(ratios[kind])}
              </span>
              {count != null ? (
                <span className="experiment-split-legend__count">
                  ({count.toLocaleString()} {count === 1 ? "image" : "images"})
                </span>
              ) : null}
            </span>
          );
        })}
      </div>

      {isValid ? (
        <div className="experiment-split-status is-valid">
          Proportions equal {sum.toFixed(2)} (100%)
        </div>
      ) : (
        <div
          className={`experiment-split-status ${
            String(errorMessage || "").startsWith("Default split")
              ? "is-warning"
              : "is-invalid"
          }`}
        >
          {errorMessage ||
            ratioError ||
            `Proportions equal ${sum.toFixed(2)}. Train, val, and test must add up to 1.00.`}
        </div>
      )}
    </section>
  );
};

const getEnumOptions = (node = {}) => {
  if (Array.isArray(node.enum) && node.enum.length) {
    return node.enum.map((option, index) => ({
      value: option,
      label: formatSplitTypeLabel(node.enumNames?.[index] || option),
    }));
  }

  return [...(node.oneOf || []), ...(node.anyOf || [])]
    .map((branch) => {
      const value = branch?.const ?? branch?.enum?.[0];
      if (value === undefined) return null;
      return {
        value,
        label: formatSplitTypeLabel(branch.title || branch.enumNames?.[0] || value),
      };
    })
    .filter(Boolean);
};

const SplitTypeRadios = ({
  title,
  required,
  options = [],
  value,
  onChange,
  disabled,
}) => (
  <div className="experiment-split-type">
    <span className="form-label mt-0">
      {getReadableExperimentName(title || "Split Type")}
      {required ? <span className="text-danger ms-1">*</span> : null}
    </span>
    <div className="experiment-technical-options experiment-technical-options--pair" role="radiogroup">
      {options.map((option) => {
        const selected = String(value) === String(option.value);
        return (
          <button
            type="button"
            key={option.value}
            className={`experiment-technical-option ${selected ? "active" : ""}`}
            onClick={() => !disabled && onChange(option.value)}
            disabled={disabled}
          >
            <span className="experiment-technical-option__title">{option.label}</span>
            <span className="experiment-technical-option__check" aria-hidden="true">
              <span />
            </span>
          </button>
        );
      })}
    </div>
  </div>
);

export const ExperimentSplitTypeField = ({
  schema: fieldSchema,
  formData,
  onChange,
  disabled,
  readonly,
  required,
}) => {
  const options = getEnumOptions(fieldSchema);
  const selectedValue = isEmptySplitValue(formData)
    ? getDefaultSplitTypeValue(fieldSchema)
    : formData;

  useEffect(() => {
    if (isEmptySplitValue(formData) && !isEmptySplitValue(selectedValue)) {
      onChange(selectedValue);
    }
  }, [formData, onChange, selectedValue]);

  return (
    <SplitTypeRadios
      title={fieldSchema?.title || "Split Type"}
      required={required}
      options={options}
      value={selectedValue}
      disabled={disabled || readonly}
      onChange={onChange}
    />
  );
};

export const ExperimentSplitField = ({
  schema: fieldSchema,
  formData,
  onChange,
  disabled,
  readonly,
  required,
  formContext = {},
}) => {
  const properties = fieldSchema?.properties || {};
  const typeKey = Object.keys(properties).find((key) =>
    isSplitTypeName(key, properties[key]?.title)
  );
  const typeOptions = getEnumOptions(properties[typeKey] || {});
  const selectedType = typeKey
    ? isEmptySplitValue(formData?.[typeKey])
      ? getDefaultSplitTypeValue(properties[typeKey])
      : formData[typeKey]
    : undefined;
  const isDefaultType = typeKey
    ? isDefaultSplitChoice(selectedType, properties[typeKey])
    : false;
  const customTypeValue = typeKey
    ? getCustomSplitTypeValue(properties[typeKey])
    : undefined;
  const ratioKeys = Object.keys(properties).filter((key) =>
    isSplitRatioName(key, properties[key]?.title)
  );
  const rows = ratioKeys.map((key) => ({
    key,
    kind: getSplitKind(key, properties[key]?.title),
    value: isDefaultType
      ? formData?.[key] ?? 0
      : formData?.[key] ?? properties[key]?.default,
    schema: properties[key],
  }));
  const overview = getDatasetOverview(formContext.schema, formContext.formData);
  const totalImages =
    formContext.totalImages != null && Number.isFinite(Number(formContext.totalImages))
      ? Number(formContext.totalImages)
      : overview?.total;
  const defaultRatiosRef = useRef(null);

  const applyRatios = (preset, { asCustom = true } = {}) => {
    const next = { ...(formData || {}) };
    rows.forEach((row) => {
      if (preset[row.kind] !== undefined) next[row.key] = preset[row.kind];
    });
    if (asCustom && typeKey && customTypeValue !== undefined) {
      next[typeKey] = customTypeValue;
    }
    onChange(next);
  };

  useEffect(() => {
    if (!isDefaultType) return;
    defaultRatiosRef.current = {
      train: asRatio(rows.find((row) => row.kind === "train")?.value),
      val: asRatio(rows.find((row) => row.kind === "val")?.value),
      test: asRatio(rows.find((row) => row.kind === "test")?.value),
    };
  }, [isDefaultType, rows]);

  useEffect(() => {
    if (!typeKey || disabled || readonly) return;
    if (isEmptySplitValue(formData?.[typeKey]) && !isEmptySplitValue(selectedType)) {
      onChange({ ...(formData || {}), [typeKey]: selectedType });
    }
  }, [disabled, formData, onChange, readonly, selectedType, typeKey]);

  return (
    <ExperimentSplitVisual
      title={fieldSchema?.title || "Split Dataset"}
      required={required}
      rows={rows}
      totalImages={totalImages}
      disabled={disabled || readonly}
      onPreset={disabled || readonly ? undefined : applyRatios}
      onChangeRatios={disabled || readonly ? undefined : applyRatios}
      allowZeroSplits={
        Boolean(formContext.allowZeroDefaultSplits) && isDefaultType
      }
      errorMessage={getSplitValidationMessage(
        fieldSchema,
        {
          ...(formData || {}),
          ...(typeKey ? { [typeKey]: selectedType } : {}),
        },
        {
          allowZeroDefaultSplits: formContext.allowZeroDefaultSplits,
        }
      )}
      extraContent={
        typeKey ? (
          typeOptions.length ? (
            <SplitTypeRadios
              title={properties[typeKey]?.title || typeKey}
              required={(fieldSchema?.required || []).includes(typeKey)}
              options={typeOptions}
              value={selectedType}
              disabled={disabled || readonly}
              onChange={(value) => {
                const next = { ...(formData || {}), [typeKey]: value };
                if (isDefaultSplitChoice(value, properties[typeKey])) {
                  if (defaultRatiosRef.current) {
                    rows.forEach((row) => {
                      if (defaultRatiosRef.current[row.kind] !== undefined) {
                        next[row.key] = defaultRatiosRef.current[row.kind];
                      }
                    });
                  }
                } else if (
                  customTypeValue !== undefined &&
                  String(selectedType) !== String(value)
                ) {
                  next[typeKey] = customTypeValue;
                  rows.forEach((row) => {
                    if (CUSTOM_SPLIT_PRESET[row.kind] !== undefined) {
                      next[row.key] = CUSTOM_SPLIT_PRESET[row.kind];
                    }
                  });
                }
                onChange(next);
              }}
            />
          ) : (
            <div className="experiment-split-type">
              <span className="form-label mt-0">
                {getReadableExperimentName(properties[typeKey]?.title || typeKey)}
                {(fieldSchema?.required || []).includes(typeKey) ? (
                  <span className="text-danger ms-1">*</span>
                ) : null}
              </span>
              <input
                className="form-control"
                value={formData?.[typeKey] ?? ""}
                disabled={disabled || readonly}
                onChange={(event) =>
                  onChange({ ...(formData || {}), [typeKey]: event.target.value })
                }
              />
            </div>
          )
        ) : null
      }
    />
  );
};

export const getTrainSectionCount = (schema) => {
  const properties = schema?.properties || {};
  const keys = Object.keys(properties);

  if (keys.length === 1 && properties[keys[0]]?.type === "object") {
    return Object.values(properties[keys[0]].properties || {}).filter(
      (item) => item?.type === "object"
    ).length;
  }

  return Object.values(properties).filter((item) => item?.type === "object").length;
};

export const getTrainSectionIds = (schema) => {
  const properties = schema?.properties || {};
  const keys = Object.keys(properties);

  if (keys.length === 1 && properties[keys[0]]?.type === "object") {
    const wrapper = keys[0];
    return Object.keys(properties[wrapper].properties || {}).map(
      (key) => `root_${wrapper}_${key}`
    );
  }

  return keys
    .filter((key) => properties[key]?.type === "object")
    .map((key) => `root_${key}`);
};

export const ExperimentEpochPresets = ({ schema, formData, onApply }) => {
  const path = findEpochsPath(schema);
  if (!path || typeof onApply !== "function") return null;

  const current = toSplitNumber(getValueAtPath(formData, path));
  const presets = [
    { id: "Fast", epochs: 10 },
    { id: "Production", epochs: 300 },
    { id: "Edge", epochs: 150 },
  ];

  return (
    <div className="experiment-epoch-presets">
      <div className="experiment-epoch-presets__label">Quick Presets</div>
      <div className="experiment-epoch-presets__grid">
        {presets.map((preset) => (
          <button
            type="button"
            key={preset.id}
            className={`experiment-epoch-preset ${
              current === preset.epochs ? "is-active" : ""
            }`}
            onClick={() => onApply(preset.epochs)}
          >
            <strong>{preset.id}</strong>
            <span>{preset.epochs}ep</span>
          </button>
        ))}
      </div>
    </div>
  );
};

export const ExperimentDatasetOverview = ({ schema, formData, totalImages }) => {
  const overview = getDatasetOverview(schema, formData);
  if (!overview) return null;
  const total =
    totalImages != null && Number.isFinite(Number(totalImages))
      ? Number(totalImages)
      : overview.total;

  const details = [
    `Dataset: ${overview.label}`,
    overview.version ? `Version: ${overview.version}` : null,
    total != null ? `Available: ${Number(total).toLocaleString()} images` : null,
    overview.classes != null ? `Classes: ${overview.classes}` : null,
    overview.resolution ? `Resolution: ${overview.resolution}` : null,
  ].filter(Boolean);

  return (
    <div className="experiment-dataset-overview container-card p-3">
      <div className="experiment-dataset-overview__icon">
        <DatasetIcon className="wf-icon" />
      </div>
      <div className="experiment-dataset-overview__content">
        <div className="second-title">Data Source Summary</div>
        <div className="small-text-secondary mt-1">
          {details.join(" • ")}
        </div>
      </div>
    </div>
  );
};

export const isMosaicObjectSchema = (schemaNode) => {
  const properties = schemaNode?.properties;
  if (!properties || schemaNode.type !== "object") return false;

  const title = normalizeName(schemaNode.title);
  const keys = Object.keys(properties);
  const hasApply = keys.some((key) =>
    normalizeName(`${key} ${properties[key]?.title}`).includes("apply")
  );
  const hasProbability = keys.some((key) => {
    const normalized = normalizeName(`${key} ${properties[key]?.title}`);
    return normalized === "p" || normalized.includes("probability");
  });

  return title.includes("mosaic") || title.includes("mixup") || (
    hasApply && hasProbability && keys.length <= 6
  );
};

export const ExperimentMosaicField = ({
  schema: fieldSchema,
  formData,
  onChange,
  disabled,
  readonly,
  required,
}) => {
  const properties = fieldSchema?.properties || {};
  const keys = Object.keys(properties);
  const applyKey = keys.find((key) =>
    normalizeName(`${key} ${properties[key]?.title}`).includes("apply")
  );
  const probabilityKey = keys.find((key) => {
    const normalized = normalizeName(`${key} ${properties[key]?.title}`);
    return normalized === "p" || normalized.includes("probability");
  });
  const otherKeys = keys.filter((key) => key !== applyKey && key !== probabilityKey);
  const applyValue = Boolean(formData?.[applyKey]);
  const canEdit = !disabled && !readonly;

  const sectionTitle = getReadableExperimentName(
    fieldSchema?.title || "Mosaic Aug"
  );
  const probabilityLabel = getReadableExperimentName(
    properties[probabilityKey]?.title || probabilityKey || "P"
  );

  return (
    <div className="experiment-nested-card experiment-mosaic-card">
      <button
        type="button"
        className={`dataset-flow-switch-option ${applyValue ? "is-checked" : ""}`}
        disabled={!canEdit}
        onClick={() =>
          canEdit &&
          applyKey &&
          onChange({
            ...(formData || {}),
            [applyKey]: !applyValue,
          })
        }
      >
        <span className="dataset-flow-switch-option__copy">
          <span className="dataset-flow-switch-option__title">
            {sectionTitle}
            {required ? <span className="text-danger ms-1">*</span> : null}
          </span>
          <span className="dataset-flow-switch-option__description">
            {applyValue
              ? `${sectionTitle} is on. Set the probability below.`
              : `Turn on to apply ${sectionTitle} during training.`}
          </span>
        </span>
        <span className="dataset-flow-switch-option__control" aria-hidden="true">
          <span className="dataset-flow-switch-option__thumb" />
        </span>
      </button>

      {applyValue && probabilityKey ? (
        <label className="experiment-split-input">
          <span className="experiment-split-input__label">
            {probabilityLabel === "P" ? "P (Probability)" : probabilityLabel}
            {(fieldSchema?.required || []).includes(probabilityKey) ? (
              <span className="text-danger ms-1">*</span>
            ) : null}
          </span>
          <input
            type="number"
            className="form-control"
            value={formData?.[probabilityKey] ?? ""}
            min={properties[probabilityKey]?.minimum}
            max={properties[probabilityKey]?.maximum}
            step="any"
            disabled={!canEdit}
            onChange={(event) =>
              onChange({
                ...(formData || {}),
                [probabilityKey]:
                  event.target.value === "" ? undefined : Number(event.target.value),
              })
            }
          />
        </label>
      ) : null}

      {applyValue
        ? otherKeys.map((key) => {
            const childSchema = properties[key] || {};
            const isBoolean = childSchema.type === "boolean";
            return (
              <label key={key} className="experiment-split-input">
                <span className="experiment-split-input__label">
                  {getReadableExperimentName(childSchema.title || key)}
                </span>
                {isBoolean ? (
                  <span>{formData?.[key] ? "On" : "Off"}</span>
                ) : (
                  <input
                    className="form-control"
                    value={formData?.[key] ?? ""}
                    disabled={!canEdit}
                    onChange={(event) =>
                      onChange({ ...(formData || {}), [key]: event.target.value })
                    }
                  />
                )}
              </label>
            );
          })
        : null}
    </div>
  );
};

export const ExperimentFieldTemplate = ({
  id,
  children,
  displayLabel,
  hidden,
  label,
  required,
  rawErrors = [],
  help,
  uiSchema = {},
  schema,
}) => {
  if (hidden) return <>{children}</>;

  const hideLabel = uiSchema?.["ui:options"]?.label === false;
  const isBoolean = schema?.type === "boolean";
  const hideFieldErrors = uiSchema?.["ui:field"] === "experimentLossWeightsField";
  const showLabel = displayLabel && !hideLabel && label && !isBoolean;
  const heading = /^(create|content)$/i.test(String(label).trim())
    ? "Choose"
    : getReadableExperimentName(label);

  return (
    <div
      className={`dataset-flow-form-field ${
        isBoolean ? "dataset-flow-form-field--boolean" : ""
      } ${schema?.type === "array" ? "dataset-flow-form-field--array" : ""}`}
    >
      {showLabel && (
        <label htmlFor={id} className="form-label">
          {heading}
          {required && <span className="text-danger ms-1">*</span>}
        </label>
      )}
      {rawErrors.length > 0 && !hideFieldErrors && (
        <div className="text-danger small fw-semibold mb-1">{rawErrors[0]}</div>
      )}
      {children}
      {(!hideLabel || rawErrors.length > 0 || help) &&
        uiSchema?.["ui:options"]?.help !== false &&
        help}
    </div>
  );
};

export const ExperimentObjectFieldTemplate = ({
  idSchema,
  title,
  description,
  properties = [],
  required,
  uiSchema = {},
  schema,
  formData,
  formContext = {},
}) => {
  const isRootObject = idSchema?.$id === "root";
  const isTransparent = uiSchema?.["ui:title"] === false;
  const stepLabel = String(formContext.currentStepLabel || "");
  const isDataStep = /data/i.test(stepLabel);
  const isModelStep = /model/i.test(stepLabel);
  const isTrainStep = /train/i.test(stepLabel) && !/start/i.test(stepLabel);
  const splitProperties = properties.filter((property) =>
    isSplitRatioName(property.name, schema?.properties?.[property.name]?.title)
  );
  const splitTypeProperties = properties.filter((property) =>
    isSplitTypeName(property.name, schema?.properties?.[property.name]?.title)
  );
  const otherProperties = properties.filter(
    (property) =>
      !isSplitRatioName(property.name, schema?.properties?.[property.name]?.title) &&
      !isSplitTypeName(property.name, schema?.properties?.[property.name]?.title)
  );
  const isSplitOnlyObject =
    !isRootObject &&
    !isTransparent &&
    splitProperties.length >= 2 &&
    otherProperties.length === 0;

  const renderProperties = (items) =>
    items.map((property) => {
      const isVersionField = /datasetversion|version/i.test(
        normalizeName(property.name)
      );

      return (
        <React.Fragment key={property.name}>
          {property.content}
          {isDataStep && isVersionField ? (
            <ExperimentDatasetOverview
              schema={formContext.schema}
              formData={formContext.formData}
              totalImages={formContext.totalImages}
            />
          ) : null}
        </React.Fragment>
      );
    });

  const splitVisual =
    splitProperties.length >= 2 ? (
      <ExperimentSplitVisual
        title={isSplitOnlyObject ? title || "Split Dataset" : "Split Dataset"}
        required={
          required ||
          splitProperties.some((property) =>
            (schema?.required || []).includes(property.name)
          )
        }
        rows={splitProperties.map((property) => {
          const typeProperty = splitTypeProperties[0];
          const typeSchema = typeProperty
            ? schema?.properties?.[typeProperty.name]
            : null;
          const typeValue = typeProperty
            ? formData?.[typeProperty.name] ?? getDefaultSplitTypeValue(typeSchema)
            : undefined;
          const useDefaultZeros = isDefaultSplitChoice(typeValue, typeSchema);
          return {
            kind: getSplitKind(
              property.name,
              schema?.properties?.[property.name]?.title
            ),
            value: useDefaultZeros
              ? formData?.[property.name] ?? 0
              : formData?.[property.name] ??
                schema?.properties?.[property.name]?.default,
          };
        })}
        totalImages={
          formContext.totalImages != null &&
          Number.isFinite(Number(formContext.totalImages))
            ? Number(formContext.totalImages)
            : getDatasetOverview(formContext.schema, formContext.formData)?.total
        }
        extraContent={
          splitTypeProperties.length
            ? splitTypeProperties.map((property) => {
                const childSchema = schema?.properties?.[property.name] || {};
                const typeMatch = findSplitTypePaths(formContext.schema).find(
                  (entry) => entry.path[entry.path.length - 1] === property.name
                );
                const typeSchema = getEnumOptions(childSchema).length
                  ? childSchema
                  : typeMatch?.schema || childSchema;
                const options = getEnumOptions(typeSchema);
                const currentValue = isEmptySplitValue(formData?.[property.name])
                  ? getDefaultSplitTypeValue(typeSchema)
                  : formData[property.name];

                return (
                  <React.Fragment key={property.name}>
                    <div className="experiment-hidden-rjsf-field" aria-hidden="true">{property.content}</div>
                    <SplitTypeRadios
                      title={childSchema.title || property.name}
                      required={(schema?.required || []).includes(property.name)}
                      options={options}
                      value={currentValue}
                      onChange={(value) => {
                        if (typeof formContext.applySplitType === "function") {
                          formContext.applySplitType(value, {
                            restoreDefault: isDefaultSplitChoice(value, typeSchema),
                            applyCustomPreset:
                              !isDefaultSplitChoice(value, typeSchema) &&
                              String(currentValue) !== String(value),
                          });
                        }
                      }}
                    />
                  </React.Fragment>
                );
              })
            : null
        }
        onPreset={
          typeof formContext.applySplitPreset === "function"
            ? (preset) => formContext.applySplitPreset(preset, { asCustom: true })
            : undefined
        }
        onChangeRatios={
          typeof formContext.applySplitPreset === "function"
            ? (preset) => formContext.applySplitPreset(preset, { asCustom: true })
            : undefined
        }
        allowZeroSplits={
          Boolean(formContext.allowZeroDefaultSplits) &&
          isDefaultSplitSelected(formContext.schema, formContext.formData)
        }
        errorMessage={getSplitValidationMessage(
          formContext.schema,
          formContext.formData,
          {
            allowZeroDefaultSplits: formContext.allowZeroDefaultSplits,
          }
        )}
      />
    ) : null;

  if (isSplitOnlyObject) {
    return splitVisual;
  }

  if (isRootObject || isTransparent) {
    return (
      <div className="experiment-object-root">
        {renderProperties(otherProperties)}
        {splitVisual}
      </div>
    );
  }

  const isNestedCard =
    Boolean(title && title !== "root") &&
    (isModelStep ||
      isTrainStep ||
      isDataStep ||
      Boolean(formContext.useNestedCards));

  return (
    <div className={isNestedCard ? "experiment-nested-card" : "rjsf-object-field"}>
      {title && title !== "root" && (
        <label className="form-label mt-0">
          {getReadableExperimentName(title)}
          {required ? <span className="text-danger ms-1">*</span> : null}
        </label>
      )}
      {description}
      {renderProperties(otherProperties)}
      {splitVisual}
    </div>
  );
};

export const applyExperimentLayoutUi = (schemaNode, uiNode = {}) => {
  if (!schemaNode || typeof schemaNode !== "object") {
    return uiNode || {};
  }

  const nextUiNode = { ...(uiNode || {}) };

  if (isSplitObjectSchema(schemaNode) && !nextUiNode["ui:field"]) {
    nextUiNode["ui:field"] = "experimentSplitField";
    nextUiNode["ui:title"] = false;
    nextUiNode["ui:options"] = {
      ...(nextUiNode["ui:options"] || {}),
      label: false,
      help: false,
    };
  }

  if (isMosaicObjectSchema(schemaNode) && !nextUiNode["ui:field"]) {
    nextUiNode["ui:field"] = "experimentMosaicField";
    nextUiNode["ui:title"] = false;
    nextUiNode["ui:options"] = {
      ...(nextUiNode["ui:options"] || {}),
      label: false,
      help: false,
    };
  }

  if (schemaNode.properties && typeof schemaNode.properties === "object") {
    Object.keys(schemaNode.properties).forEach((key) => {
      nextUiNode[key] = applyExperimentLayoutUi(
        schemaNode.properties[key],
        nextUiNode[key]
      );

      if (isSplitRatioName(key, schemaNode.properties[key]?.title)) {
        nextUiNode[key] = {
          ...(nextUiNode[key] || {}),
          "ui:widget": "hidden",
        };
      }

      if (isSplitTypeName(key, schemaNode.properties[key]?.title)) {
        nextUiNode[key] = {
          ...(nextUiNode[key] || {}),
          "ui:field": "experimentSplitTypeField",
          "ui:title": false,
          "ui:options": {
            ...(nextUiNode[key]?.["ui:options"] || {}),
            label: false,
            help: false,
          },
        };
      }
    });
  }

  if (schemaNode.items && typeof schemaNode.items === "object") {
    nextUiNode.items = applyExperimentLayoutUi(
      schemaNode.items,
      nextUiNode.items
    );
  }

  ["oneOf", "anyOf", "allOf"].forEach((key) => {
    if (!Array.isArray(schemaNode[key])) return;
    nextUiNode[key] = schemaNode[key].map((branch, index) =>
      applyExperimentLayoutUi(branch, nextUiNode[key]?.[index])
    );
  });

  if (schemaNode.dependencies && typeof schemaNode.dependencies === "object") {
    Object.keys(schemaNode.dependencies).forEach((depKey) => {
      const dependency = schemaNode.dependencies[depKey];

      if (Array.isArray(dependency?.oneOf)) {
        nextUiNode[depKey] = {
          ...(nextUiNode[depKey] || {}),
          oneOf: dependency.oneOf.map((branch, index) =>
            applyExperimentLayoutUi(branch, nextUiNode[depKey]?.oneOf?.[index])
          ),
        };
        dependency.oneOf.forEach((branch) => {
          Object.entries(branch?.properties || {}).forEach(([key, child]) => {
            if (isSplitRatioName(key, child?.title)) {
              nextUiNode[key] = {
                ...(nextUiNode[key] || {}),
                "ui:widget": "hidden",
              };
            }
            if (isSplitTypeName(key, child?.title)) {
              nextUiNode[key] = {
                ...(nextUiNode[key] || {}),
                "ui:field": "experimentSplitTypeField",
                "ui:title": false,
                "ui:options": {
                  ...(nextUiNode[key]?.["ui:options"] || {}),
                  label: false,
                  help: false,
                },
              };
            }
          });
        });
      } else if (dependency && typeof dependency === "object") {
        nextUiNode[depKey] = applyExperimentLayoutUi(
          dependency,
          nextUiNode[depKey]
        );
      }
    });
  }

  return nextUiNode;
};
